import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Check, Package, Ban, Search, Send, StickyNote, ChevronDown, ChevronUp, X, Download, Trash2, UserPlus, Copy } from 'lucide-react';

const fmt = (n, cur = 'NZD') => `$${Number(n || 0).toFixed(2)} ${cur}`;
const STATUSES = ['pending', 'paid', 'shipped', 'cancelled'];

function InviteDialog({ invite, onClose }) {
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="invite-dialog">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div><div className="h-eyebrow">Heritage Apparel Club invite</div><div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{invite.name || invite.email}</div><div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{invite.email}</div></div>
          <button onClick={onClose} className="p-1" aria-label="Close" style={{ color: 'var(--heritage-plum)' }}><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="rounded-md p-3" style={{ background: 'rgba(91,110,44,0.1)' }}>
            <div className="text-xs" style={{ color: 'var(--heritage-plum)' }}>Their invite email was sent automatically. If it doesn't arrive, copy the pieces below and send manually.</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><div className="h-eyebrow">Signup link</div><button onClick={() => copy(invite.signup_link, 'Link')} className="h-btn-outline text-xs" data-testid="invite-copy-link"><Copy size={12} className="mr-1" /> Copy link</button></div>
            <div className="rounded-md border px-3 py-2 text-xs break-all bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}>{invite.signup_link}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><div className="h-eyebrow">Email subject</div><button onClick={() => copy(invite.invite_subject, 'Subject')} className="h-btn-outline text-xs" data-testid="invite-copy-subject"><Copy size={12} className="mr-1" /> Copy</button></div>
            <div className="rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}>{invite.invite_subject}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><div className="h-eyebrow">Email body</div><button onClick={() => copy(invite.invite_body, 'Body')} className="h-btn text-xs" data-testid="invite-copy-body"><Copy size={12} className="mr-1" /> Copy body</button></div>
            <textarea rows={10} readOnly value={invite.invite_body} className="w-full rounded-md border px-3 py-2 text-xs bg-white font-mono" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} data-testid="invite-body" />
          </div>
          <div className="flex justify-end gap-2">
            <a href={`mailto:${invite.email}?subject=${encodeURIComponent(invite.invite_subject)}&body=${encodeURIComponent(invite.invite_body)}`} className="h-btn text-xs" data-testid="invite-open-mail"><Send size={12} className="mr-1" /> Open in mail app</a>
            <button onClick={onClose} className="h-btn-outline text-xs">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pending', bg: 'rgba(75,24,56,0.08)' },
    paid: { label: 'Paid', bg: 'rgba(91,110,44,0.15)' },
    shipped: { label: 'Shipped', bg: 'rgba(75,24,56,0.2)' },
    cancelled: { label: 'Cancelled', bg: 'rgba(154,44,44,0.15)' },
  };
  const s = map[status] || map.pending;
  return <span className="rounded-full px-2 py-0.5 text-[10px] h-serif tracking-wider uppercase" style={{ background: s.bg, color: 'var(--heritage-plum)' }}>{s.label}</span>;
}

function fmtAddress(a) {
  if (!a) return '';
  return [a.line1, a.line2, a.suburb, a.city, a.region, a.postcode, a.country].filter(Boolean).join(', ');
}

function ShipDialog({ order, onClose, onDone }) {
  const [courier, setCourier] = useState('');
  const [tracking, setTracking] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/shop/orders/${order.id}/mark-shipped`, { courier, tracking });
      toast.success(`${order.invoice_number} marked shipped`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.5)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} data-testid="ship-dialog">
        <div className="flex items-start justify-between">
          <div>
            <div className="h-eyebrow">Mark shipped</div>
            <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{order.invoice_number}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ color: 'var(--heritage-plum)' }}><X size={18} /></button>
        </div>
        <div className="mt-4 space-y-3">
          <label className="block">
            <div className="h-eyebrow mb-1">Courier</div>
            <input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="NZ Post, Aramex, GSS..."
              className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
              data-testid="ship-courier" />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1">Tracking number</div>
            <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Optional"
              className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
              data-testid="ship-tracking" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-btn-outline text-xs" data-testid="ship-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="h-btn text-xs" data-testid="ship-confirm"><Package size={12} className="mr-1" /> {saving ? 'Saving...' : 'Mark shipped & email'}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShopOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});
  const [shipDlg, setShipDlg] = useState(null);
  const [inviteDlg, setInviteDlg] = useState(null);
  const [notesEdit, setNotesEdit] = useState({}); // orderId -> localNotes

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shop/orders');
      setOrders(data.orders || []);
    } catch { toast.error('Failed to load orders'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => orders.filter((o) => {
    if (filter !== 'all' && o.status !== filter) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [o.invoice_number, o.customer_name, o.customer_email].some((x) => (x || '').toLowerCase().includes(s));
  }), [orders, filter, q]);

  const counts = useMemo(() => {
    const c = { all: orders.length };
    STATUSES.forEach((s) => { c[s] = orders.filter((o) => o.status === s).length; });
    return c;
  }, [orders]);

  const markPaid = async (o) => {
    if (!window.confirm(`Mark ${o.invoice_number} as PAID? Stock will be decremented.`)) return;
    try {
      await api.post(`/admin/shop/orders/${o.id}/mark-paid`);
      toast.success(`${o.invoice_number} marked paid`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const cancel = async (o) => {
    if (!window.confirm(`Cancel ${o.invoice_number}? Stock will be restored if the order was paid.`)) return;
    try {
      await api.post(`/admin/shop/orders/${o.id}/cancel`);
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete ${o.invoice_number} permanently? This cannot be undone.\n\nTip: cancel first if the order was paid so stock is returned to inventory.`)) return;
    try {
      await api.delete(`/admin/shop/orders/${o.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const resend = async (o) => {
    try {
      await api.post(`/admin/shop/orders/${o.id}/resend-confirmation`);
      toast.success('Confirmation email resent');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const createAccount = async (o) => {
    if (!o.customer_email) { toast.error('This order has no customer email.'); return; }
    try {
      const { data } = await api.post(`/admin/shop/orders/${o.id}/create-account`);
      setInviteDlg(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const saveNotes = async (o) => {
    const notes = notesEdit[o.id] ?? o.admin_notes ?? '';
    try {
      await api.post(`/admin/shop/orders/${o.id}/notes`, { notes });
      toast.success('Notes saved');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-5" data-testid="admin-shop-orders">
      <div className="h-panel-thin p-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="h-eyebrow">Store</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Orders</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Track every order from checkout to shipped. Refunds — have the customer contact Heritage and Kayla will handle it directly.</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const res = await api.get('/admin/shop/orders.csv', { responseType: 'blob' });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement('a');
              a.href = url;
              a.download = `heritage_orders_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success('CSV downloaded');
            } catch { toast.error('Export failed'); }
          }}
          className="h-btn-outline text-xs"
          data-testid="orders-export-csv"
        >
          <Download size={12} className="mr-1.5" /> Export CSV
        </button>
      </div>

      <Panel className="p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {['all', ...STATUSES].map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                className="rounded-full px-3 py-1 text-xs h-serif transition-colors"
                style={{
                  background: filter === s ? 'var(--heritage-plum)' : 'transparent',
                  color: filter === s ? 'var(--heritage-cream)' : 'var(--heritage-plum)',
                  border: '1px solid var(--heritage-plum)',
                }}
                data-testid={`shop-orders-filter-${s}`}
              >
                {s === 'all' ? 'All' : s[0].toUpperCase() + s.slice(1)} <span className="opacity-70">({counts[s] || 0})</span>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice / email / name"
              className="pl-8 pr-3 py-1.5 rounded-full text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)', minWidth: 260 }}
              data-testid="shop-orders-search" />
          </div>
        </div>
      </Panel>

      {loading && <div className="h-eyebrow">Loading...</div>}
      {!loading && filtered.length === 0 && (
        <Panel className="p-8 text-center">
          <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>No orders for this filter.</div>
        </Panel>
      )}

      <div className="space-y-3">
        {filtered.map((o) => {
          const isOpen = !!expanded[o.id];
          return (
            <Panel key={o.id} className="p-4" data-testid={`shop-order-${o.invoice_number}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-display text-xl" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</span>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>
                    {o.customer_name} · {o.customer_email}{o.customer_phone ? ` · ${o.customer_phone}` : ''}
                  </div>
                  <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>
                    {new Date(o.created_at).toLocaleString('en-NZ')} · {(o.items || []).length} item{(o.items || []).length === 1 ? '' : 's'} · <b>{fmt(o.total, o.currency)}</b>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {o.status === 'pending' && (
                    <button onClick={() => markPaid(o)} className="h-btn text-xs" data-testid={`shop-order-paid-${o.invoice_number}`}><Check size={12} className="mr-1" /> Mark paid</button>
                  )}
                  {o.status === 'paid' && (
                    <button onClick={() => setShipDlg(o)} className="h-btn text-xs" data-testid={`shop-order-ship-${o.invoice_number}`}><Package size={12} className="mr-1" /> Mark shipped</button>
                  )}
                  {o.status !== 'cancelled' && (
                    <button onClick={() => resend(o)} className="h-btn-outline text-xs" data-testid={`shop-order-resend-${o.invoice_number}`}><Send size={12} className="mr-1" /> Resend receipt</button>
                  )}
                  <button onClick={() => createAccount(o)} className="h-btn-outline text-xs" data-testid={`shop-order-create-account-${o.invoice_number}`} title="Create Heritage Apparel Club account for this customer">
                    <UserPlus size={12} className="mr-1" /> Create account
                  </button>
                  {o.status !== 'cancelled' && o.status !== 'shipped' && (
                    <button onClick={() => cancel(o)} className="h-btn-outline text-xs" style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }} data-testid={`shop-order-cancel-${o.invoice_number}`}><Ban size={12} className="mr-1" /> Cancel</button>
                  )}
                  <button onClick={() => remove(o)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100 px-2 py-1" style={{ color: '#9A2C2C' }} data-testid={`shop-order-delete-${o.invoice_number}`} title="Delete permanently">
                    <Trash2 size={12} /> Delete
                  </button>
                  <button onClick={() => toggle(o.id)} className="h-btn-outline text-xs">
                    {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="h-eyebrow text-[10px]">Items</div>
                      <ul className="mt-2 text-sm space-y-1.5" style={{ color: 'var(--heritage-plum)' }}>
                        {(o.items || []).map((li) => (
                          <li key={li.sku} className="flex items-center justify-between gap-3">
                            <span>{li.qty}× <span className="h-serif">{li.name}</span> <span className="opacity-70 text-xs">{[li.size && `Size ${li.size}`, li.colour].filter(Boolean).join(' · ')}</span></span>
                            <span className="h-serif">${Number(li.line_total).toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="h-divider my-3" />
                      <div className="text-xs space-y-0.5" style={{ color: 'var(--heritage-plum)' }}>
                        <div className="flex justify-between"><span>Subtotal</span><span>${Number(o.subtotal || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span>Shipping</span><span>${Number(o.shipping || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between h-serif"><span>Total</span><span>{fmt(o.total, o.currency)}</span></div>
                      </div>
                    </div>
                    <div>
                      <div className="h-eyebrow text-[10px]">Shipping</div>
                      <div className="text-sm mt-1" style={{ color: 'var(--heritage-plum)' }}>{fmtAddress(o.shipping_address)}</div>
                      {o.delivery_notes && <div className="text-xs opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Notes: {o.delivery_notes}</div>}
                      {o.status === 'shipped' && (
                        <div className="mt-2 text-xs" style={{ color: 'var(--heritage-plum)' }}>
                          <div className="h-eyebrow text-[10px]">Tracking</div>
                          <div className="h-serif">{o.shipping_courier || ''} {o.shipping_tracking || ''}</div>
                        </div>
                      )}
                      <div className="mt-3">
                        <div className="h-eyebrow text-[10px]">Payment</div>
                        <div className="text-xs" style={{ color: 'var(--heritage-plum)' }}>
                          {o.stripe_session_id ? <span>Stripe · {o.stripe_session_id.substring(0, 24)}...</span> : <span>Manual</span>}
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="h-eyebrow text-[10px] flex items-center gap-1"><StickyNote size={11} /> Admin notes</div>
                        <textarea
                          rows={3}
                          defaultValue={o.admin_notes || ''}
                          onChange={(e) => setNotesEdit((n) => ({ ...n, [o.id]: e.target.value }))}
                          placeholder="Internal notes visible to admins only."
                          className="w-full rounded-md border px-2 py-1.5 text-xs bg-white mt-1"
                          style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                          data-testid={`shop-order-notes-${o.invoice_number}`}
                        />
                        <button onClick={() => saveNotes(o)} className="h-btn-outline text-xs mt-2" data-testid={`shop-order-notes-save-${o.invoice_number}`}>Save notes</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Panel>
          );
        })}
      </div>

      {shipDlg && <ShipDialog order={shipDlg} onClose={() => setShipDlg(null)} onDone={() => { setShipDlg(null); load(); }} />}
      {inviteDlg && <InviteDialog invite={inviteDlg} onClose={() => setInviteDlg(null)} />}
    </div>
  );
}
